import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Loader2, Save, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { contentService, adminService } from '../../services/api';

const AdminOtherServices = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  
  const [content, setContent] = useState({
    showOtherServices: true,
    showImportantInfo: true,
    otherServices: [
      {
        title: '保險安排',
        titleEn: 'Insurance Arrangement',
        description: '協助僱主購買合適的僱員補償保險及醫療保險。',
        descriptionEn: 'Assist employers in purchasing appropriate employee compensation and medical insurance.',
        items: ['僱員補償保險', '醫療保險', '意外保障'],
        itemsEn: ['Employee Compensation Insurance', 'Medical Insurance', 'Accident Protection']
      },
      {
        title: '醫療檢查',
        titleEn: 'Medical Examination',
        description: '安排女傭進行所需的醫療檢查，確保健康狀況良好。',
        descriptionEn: 'Arrange necessary medical examinations for helpers to ensure good health condition.',
        items: ['全身體格檢查', 'X光檢查', '懷孕檢測'],
        itemsEn: ['Full Physical Examination', 'X-Ray Examination', 'Pregnancy Test']
      },
      {
        title: '簽證申請',
        titleEn: 'Visa Application',
        description: '全程跟進簽證申請，確保順利及時。',
        descriptionEn: 'Full follow-up on visa applications to ensure smooth and timely processing.',
        items: ['入境處申請', '領事館公證', '簽證續期'],
        itemsEn: ['Immigration Application', 'Consulate Notarization', 'Visa Renewal']
      }
    ],
    importantInfo: [
      '服務保證：我們提供兩年合約保證，如女傭不合適可免費更換。',
      '付款方式：接受現金、支票及轉帳付款。',
      '處理時間：海外女傭約8-12星期，現成女傭約4-6星期。',
      '免費服務：提供免費咨詢、配對及跟進服務。',
      '特別優惠：定期推出優惠計劃，請留意我們的最新資訊。'
    ],
    importantInfoEn: [
      'Service Guarantee: We provide a two-year contract guarantee with free replacement if the helper is not suitable.',
      'Payment Methods: Cash, cheque, and bank transfer accepted.',
      'Processing Time: Overseas helpers approximately 8-12 weeks, local helpers approximately 4-6 weeks.',
      'Free Services: Free consultation, matching, and follow-up services.',
      'Special Offers: Regular promotions available, please check our latest news.'
    ]
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      if (response.data) {
        // Ensure otherServices have English fields
        const otherServices = (response.data.otherServices || []).map(s => ({
          ...s,
          titleEn: s.titleEn || '',
          descriptionEn: s.descriptionEn || '',
          itemsEn: s.itemsEn || s.items?.map(() => '') || []
        }));
        
        // Ensure importantInfoEn matches importantInfo length
        const importantInfo = response.data.importantInfo || [];
        const importantInfoEn = response.data.importantInfoEn || [];
        while (importantInfoEn.length < importantInfo.length) {
          importantInfoEn.push('');
        }
        
        setContent(prev => ({
          showOtherServices: response.data.showOtherServices !== undefined ? response.data.showOtherServices : true,
          showImportantInfo: response.data.showImportantInfo !== undefined ? response.data.showImportantInfo : true,
          otherServices: otherServices.length > 0 ? otherServices : prev.otherServices,
          importantInfo: importantInfo.length > 0 ? importantInfo : prev.importantInfo,
          importantInfoEn: importantInfoEn,
        }));
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentContent = await contentService.get();
      const updatedContent = {
        ...currentContent.data,
        showOtherServices: content.showOtherServices,
        showImportantInfo: content.showImportantInfo,
        otherServices: content.otherServices,
        importantInfo: content.importantInfo,
        importantInfoEn: content.importantInfoEn
      };
      
      await adminService.content.update(updatedContent);
      
      toast({
        title: '成功 / Success',
        description: '內容已保存 / Content saved'
      });
    } catch (error) {
      console.error('Error saving:', error);
      toast({
        title: '錯誤 / Error',
        description: '保存失敗，請再試一次 / Save failed, please try again',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Toggle handlers
  const toggleShowOtherServices = () => {
    setContent({ ...content, showOtherServices: !content.showOtherServices });
  };

  const toggleShowImportantInfo = () => {
    setContent({ ...content, showImportantInfo: !content.showImportantInfo });
  };

  // Other Services handlers - Chinese
  const updateOtherServiceTitle = (index, value) => {
    const newServices = [...content.otherServices];
    newServices[index].title = value;
    setContent({ ...content, otherServices: newServices });
  };

  const updateOtherServiceTitleEn = (index, value) => {
    const newServices = [...content.otherServices];
    newServices[index].titleEn = value;
    setContent({ ...content, otherServices: newServices });
  };

  const updateOtherServiceDescription = (index, value) => {
    const newServices = [...content.otherServices];
    newServices[index].description = value;
    setContent({ ...content, otherServices: newServices });
  };

  const updateOtherServiceDescriptionEn = (index, value) => {
    const newServices = [...content.otherServices];
    newServices[index].descriptionEn = value;
    setContent({ ...content, otherServices: newServices });
  };

  const updateOtherServiceItem = (serviceIndex, itemIndex, value) => {
    const newServices = [...content.otherServices];
    newServices[serviceIndex].items[itemIndex] = value;
    setContent({ ...content, otherServices: newServices });
  };

  const updateOtherServiceItemEn = (serviceIndex, itemIndex, value) => {
    const newServices = [...content.otherServices];
    if (!newServices[serviceIndex].itemsEn) {
      newServices[serviceIndex].itemsEn = [];
    }
    newServices[serviceIndex].itemsEn[itemIndex] = value;
    setContent({ ...content, otherServices: newServices });
  };

  const addOtherServiceItem = (serviceIndex) => {
    const newServices = [...content.otherServices];
    newServices[serviceIndex].items.push('');
    if (!newServices[serviceIndex].itemsEn) {
      newServices[serviceIndex].itemsEn = [];
    }
    newServices[serviceIndex].itemsEn.push('');
    setContent({ ...content, otherServices: newServices });
  };

  const removeOtherServiceItem = (serviceIndex, itemIndex) => {
    const newServices = [...content.otherServices];
    newServices[serviceIndex].items = newServices[serviceIndex].items.filter((_, i) => i !== itemIndex);
    if (newServices[serviceIndex].itemsEn) {
      newServices[serviceIndex].itemsEn = newServices[serviceIndex].itemsEn.filter((_, i) => i !== itemIndex);
    }
    setContent({ ...content, otherServices: newServices });
  };

  // Important Info handlers
  const updateImportantInfo = (index, value) => {
    const newInfo = [...content.importantInfo];
    newInfo[index] = value;
    setContent({ ...content, importantInfo: newInfo });
  };

  const updateImportantInfoEn = (index, value) => {
    const newInfoEn = [...(content.importantInfoEn || [])];
    newInfoEn[index] = value;
    setContent({ ...content, importantInfoEn: newInfoEn });
  };

  const addImportantInfo = () => {
    setContent({ 
      ...content, 
      importantInfo: [...content.importantInfo, ''],
      importantInfoEn: [...(content.importantInfoEn || []), '']
    });
  };

  const removeImportantInfo = (index) => {
    setContent({ 
      ...content, 
      importantInfo: content.importantInfo.filter((_, i) => i !== index),
      importantInfoEn: (content.importantInfoEn || []).filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>其他服務管理 / Other Services</h1>
        <p className="text-gray-600 mt-2">編輯服務頁面的其他服務及重要資訊（中英文）/ Edit Other Services and Important Info (Chinese and English)</p>
      </div>

      <div className="space-y-6">
        {/* Other Services (3 cards) */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <div className="flex justify-between items-center">
              <CardTitle style={{ color: '#00b6a5' }}>其他服務（3個）/ Other Services</CardTitle>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">
                  {content.showOtherServices ? (
                    <span className="flex items-center text-green-600">
                      <Eye className="h-4 w-4 mr-1" /> 顯示中
                    </span>
                  ) : (
                    <span className="flex items-center text-gray-400">
                      <EyeOff className="h-4 w-4 mr-1" /> 已隱藏
                    </span>
                  )}
                </span>
                <Switch
                  checked={content.showOtherServices}
                  onCheckedChange={toggleShowOtherServices}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className={`pt-6 space-y-6 ${!content.showOtherServices ? 'opacity-50' : ''}`}>
            {content.otherServices.map((service, serviceIndex) => (
              <div key={serviceIndex} className="p-4 bg-gray-50 rounded-lg space-y-4">
                {/* Chinese fields */}
                <div className="border-b pb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">中文內容 Chinese Content</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">服務標題（中文）</label>
                      <Input
                        value={service.title}
                        onChange={(e) => updateOtherServiceTitle(serviceIndex, e.target.value)}
                        placeholder="例如: 保險安排"
                        disabled={!content.showOtherServices}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-2">服務描述（中文）</label>
                    <Textarea
                      value={service.description}
                      onChange={(e) => updateOtherServiceDescription(serviceIndex, e.target.value)}
                      placeholder="描述此服務..."
                      rows={2}
                      disabled={!content.showOtherServices}
                    />
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-2">服務項目（中文）</label>
                    {service.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center gap-2 mb-2">
                        <Input
                          value={item}
                          onChange={(e) => updateOtherServiceItem(serviceIndex, itemIndex, e.target.value)}
                          placeholder="輸入服務項目..."
                          disabled={!content.showOtherServices}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeOtherServiceItem(serviceIndex, itemIndex)}
                          className="text-red-500"
                          disabled={!content.showOtherServices}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => addOtherServiceItem(serviceIndex)}
                      disabled={!content.showOtherServices}
                    >
                      <Plus className="h-4 w-4 mr-1" /> 新增項目
                    </Button>
                  </div>
                </div>

                {/* English fields */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 mb-3">English Content 英文內容</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-blue-700">Service Title (English)</label>
                      <Input
                        value={service.titleEn || ''}
                        onChange={(e) => updateOtherServiceTitleEn(serviceIndex, e.target.value)}
                        placeholder="e.g., Insurance Arrangement"
                        className="border-blue-300"
                        disabled={!content.showOtherServices}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-2 text-blue-700">Service Description (English)</label>
                    <Textarea
                      value={service.descriptionEn || ''}
                      onChange={(e) => updateOtherServiceDescriptionEn(serviceIndex, e.target.value)}
                      placeholder="Describe this service in English..."
                      rows={2}
                      className="border-blue-300"
                      disabled={!content.showOtherServices}
                    />
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-2 text-blue-700">Service Items (English)</label>
                    {service.items.map((_, itemIndex) => (
                      <div key={itemIndex} className="flex items-center gap-2 mb-2">
                        <Input
                          value={service.itemsEn?.[itemIndex] || ''}
                          onChange={(e) => updateOtherServiceItemEn(serviceIndex, itemIndex, e.target.value)}
                          placeholder="Enter service item in English..."
                          className="border-blue-300"
                          disabled={!content.showOtherServices}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Important Information */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <div className="flex justify-between items-center">
              <CardTitle style={{ color: '#00b6a5' }}>重要資訊 / Important Information</CardTitle>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">
                  {content.showImportantInfo ? (
                    <span className="flex items-center text-green-600">
                      <Eye className="h-4 w-4 mr-1" /> 顯示中
                    </span>
                  ) : (
                    <span className="flex items-center text-gray-400">
                      <EyeOff className="h-4 w-4 mr-1" /> 已隱藏
                    </span>
                  )}
                </span>
                <Switch
                  checked={content.showImportantInfo}
                  onCheckedChange={toggleShowImportantInfo}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className={`pt-6 space-y-4 ${!content.showImportantInfo ? 'opacity-50' : ''}`}>
            <p className="text-sm text-gray-600 mb-4">
              格式提示：請以標題：內容的格式輸入，例如服務保證：我們提供兩年合約保證
            </p>
            {content.importantInfo.map((info, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={info}
                    onChange={(e) => updateImportantInfo(index, e.target.value)}
                    placeholder="例如: 服務保證：我們提供兩年合約保證，如女傭不合適可免費更換。"
                    disabled={!content.showImportantInfo}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeImportantInfo(index)}
                    className="text-red-500"
                    disabled={!content.showImportantInfo}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <Input
                    value={content.importantInfoEn?.[index] || ''}
                    onChange={(e) => updateImportantInfoEn(index, e.target.value)}
                    placeholder="English translation, e.g., Service Guarantee: We provide a two-year contract guarantee..."
                    className="border-blue-300"
                    disabled={!content.showImportantInfo}
                  />
                </div>
              </div>
            ))}
            <Button 
              variant="outline" 
              onClick={addImportantInfo}
              disabled={!content.showImportantInfo}
            >
              <Plus className="h-4 w-4 mr-2" /> 新增資訊 / Add Info
            </Button>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end pb-24">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            style={{ backgroundColor: '#00b6a5' }}
            className="text-white"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Save className="h-5 w-5 mr-2" />
            )}
            保存所有更改 / Save All Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminOtherServices;
